"use strict";
/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */

var EventEmitter = require("eventemitter2").EventEmitter2;
var Promise = require("promise6");
var get_ice_servers = require("./ice_servers");
var asap = require("asap");
var par = require("par");
var noop = require("noop2");
var Deferred = par(require("promise-defer"), Promise);

// Wait before creating new connection for call renegotation
var RENEGOTIATE_DELAY = 500;
var RENEGOTIATE_WAIT = 2 * 1000;
var STAT_CHECK_INTERVAL = 2000;

module.exports = makeCall;

function makeCall(options) {
	return (new Call(options)).init();
}

/**
 * Constructor for making calls
 * @param {Object} options Options for the call. Should contain `dispatcher`, `RTC`, `initiator`,`prefix`, `self`, `other`
 */
function Call(options) {
	EventEmitter.call(this);

	var dispatcher = options.dispatcher;

	this.tracker = dispatcher.createTracker();
	this.dispatcher = dispatcher;
	this.RTC = options.RTC;

	this.initiator = options.initiator;
	this.prefix = options.prefix;
	this.self = options.self;
	this.other = options.other;
	this.skipIce = options.skipIce;
	this.voiceOnly = options.voiceOnly;
	this.lockable = options.locked;
	this.locked = options.locked;
	this.host = options.host;

	this.rtc = null;
	if (options.logger)
		this.logger = options.logger;
	this.readyCount = 0;
	this.event_buffer = [];
	this.ice_buffer = [];
	this.has_remote_sdp = false;
	this.state = "started";
	this.successful = false;
	this._hangup = false;
	this._isNegotiating = false;
	this._negotiateTimer = null;
}

// Inherit all EventEmitter methods
Call.prototype = Object.create(EventEmitter.prototype);

Call.prototype.rtc = null;
Call.prototype.stream = null;
Call.prototype.readyCount = 0;
Call.prototype.event_buffer = null;
Call.prototype.ice_buffer = null;
Call.prototype.has_remote_sdp = false;
Call.prototype.state = "invalid";
Call.prototype.successful = false;
Call.prototype.voiceOnly = false;
Call.prototype.lockable = false;
Call.prototype.locked = false;
Call.prototype.unlockWait = null;
Call.prototype.stat_timer = null;
Call.prototype.logger = noop;

// Public methods
Call.prototype.accept = accept;
Call.prototype.deny = deny;
Call.prototype.hangup = hangup;
Call.prototype.conference = conference;
Call.prototype.redirect = redirect;
Call.prototype.renegotiate = renegotiate;
Call.prototype.destroy = destroy;
Call.prototype.unlock = unlock;
Call.prototype.isSameRoom = isSameRoom;
Call.prototype.switch = switchCamera;

// Initiation
Call.prototype.init = init;
Call.prototype.initRTC = initRTC;

// Buffering RTC events until ready to send
Call.prototype.isReady = isReady;
Call.prototype.bufferEvent = bufferEvent;
Call.prototype.flushBuffer = flushBuffer;

// Buffering ICE candidates until SDP gets processed
Call.prototype.bufferIce = bufferIce;
Call.prototype.flushIce = flushIce;

// Helpers for emitting events
Call.prototype.emitSelf = emitSelf;
Call.prototype.emitRoom = emitRoom;
Call.prototype.emitOther = emitOther;

// General helpers
Call.prototype.setState = setState;
Call.prototype.makeError = makeError;
Call.prototype.bind = bind;

/**
 * Accept the call and begin connecting
 * @return {Promise} resolves once the RTC connection has been initialized and events were set up
 */
function accept() {
	if (this.state !== "receiving")
		return Promise.resolve();
	return this.initRTC().then(this.bind(notify_accept)).catch(this.makeError());
}

/**
 * Deny the call and destroy the Call
 * @param  {String}  reason Optional reason for denying the call
 * @return {Promise}        Resovles once call is destroyed
 */
function deny(reason) {
	if (this.state !== "receiving")
		return Promise.resolve();
	this.emitRoom("deny", {
		reason: reason
	});
	return this.destroy().catch(this.makeError());
}

/**
 * Hangs up on the current call and destroys it
 * @param  {String}  reason Optional reason for hanging up the call
 * @return {Promise}        Resolves once call is destroyed
 */
function hangup(reason) {
	var state = this.state;
	this._hangup = true;
	if (state === "closed")
		return Promise.resolve();

	this.emitRoom("hangup", {
		reason: reason
	});
	return this.destroy().catch(this.makeError());
}

/**
 * Notifies the people in the call that a conference call is about to be made
 * @param  {String} who  Id of the person being added to the call
 * @param  {String} room The room that will be used to negotiate the connections
 * @return {Promise}     Resolves once the notification has been sent
 */
function conference(who, room, voiceOnly) {
	var state = this.state;
	if (state !== "connected")
		return Promise.reject(new Error("Cannot notify conference when not connected"));

	return Promise.resolve(this.emitRoom("conference", {
		room: room,
		who: who,
		voiceOnly: voiceOnly
	}));
}

/**
 * Redirect the other participant in the call to another person
 * @param  {String}  to The id of the person to redirect the call to
 * @return {Promise}    Resolves once the notification has been sent
 */
function redirect(to) {
	if (this.state !== "connected")
		return Promise.reject(new Error("Cannot attempt to redirect when not connected"));

	return Promise.resolve(this.emitOther("redirect", {
		to: to,
		voiceOnly: this.voiceOnly,
	}));
}

/**
 * Notifies the participants that they should renegotiate their calls
 * @param  {String} reason Optional reason for the renegotiation
 */
function renegotiate(reason) {
	this.readyCount = 0;
	this.emitRoom("renegotiate", {
		reason: reason
	});
}

/**
 * Unlocks the call and allows WEBRTC negotiation to proceed
 */
function unlock() {
	this.locked = false;
	if (this.unlockWait) {
		this.unlockWait.resolve();
		this.unlockWait = null;
	}
}


/**
 * Destroyes the call. Cleans the RTC connection, clears the tracker. Removes references.
 * Emits `close` events. Changes state to `closed`
 * @return {Promise} Resolves once everything has been destroyed
 */
function destroy() {
	if (this.state === "closed") return Promise.resolve();

	clearInterval(this.stat_timer);

	this.stream = null;

	this.setState("closed");

	return Promise.resolve().then(function () {
		this.emit("close");

		this.tracker.off();
		this.tracker = null;

		if (this.rtc) {
			this.rtc.clean();
			this.rtc = null;
		}

		this._negotiateTimer = null;
		this.event_buffer = null;
		this.dispatcher = null;
		this.RTC = null;
	}.bind(this));
}

/**
 * Initializes the call
 * @return {Promise} Resolves to Call once all listeners are set up and the state is set
 */
function init() {
	return Promise
		.resolve()
		.then(this.bind(listen_events))
		.then(this.bind(set_initial_state))
		.then(function () {
			return this;
		}.bind(this));
}

/**
 * Sets the initial state based on whether the call is being made as the initiator or not
 */
function set_initial_state() {
	if (this.initiator)
		this.setState("calling");
	else
		this.setState("receiving");
}

/**
 * Listens on events related to the call
 */
function listen_events() {
	var tracker = this.tracker;
	var prefix = this.prefix;
	var self = this.self;
	var other = this.other;

	tracker.on(make_path(prefix, "accept"), this.bind(handle_accept));
	tracker.on(make_path(prefix, "deny"), this.bind(handle_deny));
	tracker.on(make_path(prefix, "hangup"), this.bind(handle_hangup));
	tracker.on(make_path(prefix, "conference"), this.bind(handle_conference));
	tracker.on(make_path(prefix, "renegotiate"), this.bind(handle_renegotiate));

	tracker.on(make_path(prefix, self, "ready"), this.bind(handle_ready));
	tracker.on(make_path(prefix, other, "ready"), this.bind(handle_ready));

	tracker.on(make_path(prefix, self, "redirect"), this.bind(handle_redirection));

	tracker.on(make_path(prefix, other, "ICE"), this.bind(handle_remote_ice));
	tracker.on(make_path(prefix, other, "SDP"), this.bind(handle_remote_sdp));
}

/**
 * Handles events on the call being accepted via MQTT.
 * Initializes RTC if calling, destroys call if receiving
 * @param  {Object} data The payload of the MQTT event
 */
function handle_accept() {
	var state = this.state;
	if (state === "calling")
		this.initRTC().then(par(asap, this.emit.bind(this, "accept"))).catch(this.makeError());
	else if (state === "receiving")
		this.destroy();
}

/**
 * Handles events on the call being denied via MQTT.
 * If in calling state, destroys the call and emits `deny` event.
 * If receiving, destroys the call
 * @param  {Object} data Payload of the event. Should contain a "reason"
 */
function handle_deny(data) {
	var state = this.state;
	var reason = (data || {}).reason || "";
	if (state === "receiving")
		return this.destroy();
	else if (state === "calling")
		return asap(function () {
			this.emit("deny", reason);
			this.destroy();
		}.bind(this));
}

/**
 * Handles events on the call being hung up via MQTT
 * Destroys call if in a valid state
 * @param  {Object} data The payload of the event. Should contain a "reason"
 */
function handle_hangup(data) {
	var valid_state = ["calling", "receiving", "connecting", "connected"].indexOf(this.state) !== -1;
	var reason = (data || {}).reason || "";
	if (!valid_state)
		return this.makeError()(new Error("Got hangup event in wrong state: " + this.state));
	this._hangup = true;
	this.emit("hangup", reason);
	return this.destroy().catch(this.makeError());
}

/**
 * Handles events on the call inviting a person to a new conference call
 * @param  {Object} data The payload of the event. Should contain `who`, and `room`
 */
function handle_conference(data) {
	asap(this.emit.bind(this, "conference", data));
}

/**
 * Handles renegotation requests by closing the current adapter and creating a new connection
 * @param  {Object} data The payload of the event. May contain a `reason`
 */
function handle_renegotiate(data) {
	if ((this.state !== "connected") && (this.state !== "connecting"))
		return;

	if (this._isNegotiating)
		return;

	this._isNegotiating = true;
	setTimeout(function () {
		this._isNegotiating = false;
	}.bind(this), RENEGOTIATE_WAIT);

	this.has_remote_sdp = false;
	if (this.readyCount)
		this.readyCount -= 2;

	if (this.lockable)
		this.locked = true;


	this.emit("renegotiating", data);
	wait(RENEGOTIATE_DELAY)
		.then(this.rtc.clean.bind(this.rtc))
		.then(par(wait, RENEGOTIATE_DELAY))
		.then(initRTC.bind(this))
		.catch(this.makeError());
}

function wait(time) {
	return new Promise(function (resolve) {
		setTimeout(resolve, time);
	});
}

/**
 * Initializes the RTC adapter. Sets state to `connecting`
 * @return {Promise} Resolves once adapter is initilized and events are being listened to
 */
function initRTC() {
	// Account for race conditions when initRTC will be executed after the call is closed
	if (this.state === "closed")
		return;

	this.setState("connecting");

	if (this.locked) {
		this.unlockWait = Deferred();
		return this.unlockWait.promise.then(initRTC.bind(this));
	}

	return get_ice_servers(this.skipIce, this.host)
		.then(this.bind(init_rtc_with_ice))
		.then(this.bind(handle_rtc_initialized))
		.then(this.bind(handle_rtc_events))
		.then(this.bind(notify_ready));
}

/**
 * Handles ice servers being received, initializes a new RTC adapter instace
 * @param  {Array}   iceServers Array of Ice server information
 * @return {Promise}            Resolves to instance of RTC adapter
 */
function init_rtc_with_ice(iceServers) {
	this.logger("D", "Got ice servers" + iceServers);
	if (this.state === "closed") throw new Error("State closed");
	var RTC = this.RTC;
	var initiator = this.initiator;
	var voiceOnly = this.voiceOnly;

	return RTC.init({
		isInitiator: initiator,
		iceServers: iceServers,
		voiceOnly: voiceOnly
	});
}

/**
 * Called once the RTC connection has been initialized.
 * Saves a reference to the connection, if already closed, destroys the connection
 * @param  {RTC} connection Instance of RTC adapter
 * @return {RTC}            Returns the RTC connection if successful
 */
function handle_rtc_initialized(connection) {
	this.logger("D", "Got RTC connection" + connection);
	if (this.state === "closed") {
		connection.clean();
		throw new Error("RTC finished initializing after call was destroyed");
	}
	this.rtc = connection;
	return connection;
}

/**
 * Handles events on the RTC connection
 */
function handle_rtc_events() {
	var rtc = this.rtc;
	this.logger("D", "Attaching RTC events");

	if (this.state === "closed") {
		rtc.clean();
		throw new Error("State closed");
	}

	rtc.on("ice", this.bind(handle_local_ice));
	rtc.on("sdp", this.bind(handle_local_sdp));
	rtc.on("stream", this.bind(handle_rtc_stream));
	rtc.once("error", this.bind(handle_rtc_error));
	rtc.once("close", this.bind(handle_rtc_close));
}

/**
 * Called when the RTC connection has got the video stream set up
 * Sets the state to `connected`
 * @param  {Object} stream Data representing the stream
 */
function handle_rtc_stream(stream) {
	if (this.state !== "connecting")
		return this.makeError()("Got stream when not connecting");

	stream.voiceOnly = this.voiceOnly;

	this.stream = stream;

	clearInterval(this.stat_timer);
	this.stat_timer = setInterval(update_stats.bind(this), STAT_CHECK_INTERVAL);

	update_stats.call(this);
}

function update_stats() {
	this.rtc.getStats().then(handle_stats.bind(this)).catch(this.makeError);
}

function handle_stats(stats) {
	if (this.state === "connecting") {
		if (stats.hasData) {
			asap(this.emit.bind(this, "stream", this.stream));

			this.successful = true;

			this.setState("connected");
		}
	} else {
		clearInterval(this.stat_timer);
		this.stat_timer = null;
	}
}

/**
 * Called if there was an error in the RTC connection
 * Emits `error` event, destroys the call
 * @param  {Error} error The error that occured
 */
function handle_rtc_error(error) {
	console.debug(error);
	this.makeError()(error);
	this.destroy();
}

/**
 * Called once the RTC connecion has closed. Destroys the call.
 */
function handle_rtc_close() {
	this.emitSelf("disconnected");
	this.emitRoom("disconnected");
	if (this._hangup)
		this.destroy();
	else {
		this.rtc.limitBandwidth();
		this.renegotiate("Call Drop");
	}
}

/**
 * Called when RTC connection has a new ICE candidate.
 * If both parties aren't ready yet, buffers the event. Else emits `ICE` event to MQTT
 * @param  {Object} ice The Ice Candidate data
 */
function handle_local_ice(ice) {
	if (this.isReady()) {
		this.emitSelf("ICE", ice);
	} else this.bufferEvent("ICE", ice);
}

/**
 * Called once the RTC connection has the local SDP data
 * If both parties aren't ready yet, buffers the event. Else emits `SDP` event to MQTT
 * @param  {Object} sdp Local SDP data
 */
function handle_local_sdp(sdp) {
	if (this.isReady()) {
		this.emitSelf("SDP", sdp);
	} else this.bufferEvent("SDP", sdp);
}

/**
 * Called when the remote party has a new ICE candidate. Sends it to the RTC adapter
 * @param  {Object} ice ICE candidate data
 */
function handle_remote_ice(ice) {
	if (this.has_remote_sdp)
		this.rtc.addICE(ice).catch(this.makeError());
	else this.bufferIce(ice);
}

/**
 * Called when the remote party has their SDP data. Sends it to the RTC adapter
 * @param  {Object} sdp The SDP data
 */
function handle_remote_sdp(sdp) {
	this.has_remote_sdp = true;
	this.rtc.addSDP(sdp).then(flushIce.bind(this)).catch(this.makeError());
}

/**
 * Buffers a remote ice candidate to be used after SDP has been set
 * @param  {Object} ice The ice candidate
 */
function bufferIce(ice) {
	this.ice_buffer.push(ice);
}

/**
 * Goes through all the remote ice candidates that were buffered and adds them
 */
function flushIce() {
	var buffer = this.ice_buffer;
	var ice;
	while ((ice = buffer.shift()))
		this.rtc.addICE(ice).catch(this.makeError());
}

/**
 * Caleld when the remote party has notified that they wish to redirect the call
 * @param  {Object} redirection Payload with redirection information containing `to`
 */
function handle_redirection(redirection) {
	this.emit("redirect", redirection);
}

/**
 * Whether both parties are ready to exchange ICE and SDP data
 * @return {Boolean} Whether they are both ready or not
 */
function isReady() {
	return this.readyCount >= 2;
}

/**
 * Called when either one of the parties says they are ready.
 * When both are ready, the event buffer gets flushed.
 */
function handle_ready() {
	this.readyCount++;
	if (this.isReady())
		this.flushBuffer();
}

/**
 * Buffers an event to be emitted later
 * @param  {String} type The type of event being buffered (ICE or SDP)
 * @param  {Object} data The data to emit with the event
 */
function bufferEvent(type, data) {
	this.event_buffer.push({
		type: type,
		data: data
	});
}

/**
 * Flushes the event buffer, emitting all events
 */
function flushBuffer() {
	var events = this.event_buffer;
	var event;
	while ((event = events.shift()))
		this.emitSelf(event.type, event.data);
}

/**
 * Notifies the room that the call has been accepted
 */
function notify_accept() {
	this.emitRoom("accept");
}

/**
 * Notifies the room that the current person is ready to exchange RTC data
 */
function notify_ready() {
	this.emitSelf("ready");
}

function switchCamera(type){
	var self = this;
	return this.rtc.switchCamera(self, type).catch(function(err){
		console.debug(err);
	});
}

/**
 * Emits data related to oneself to the Call room
 * @param  {String} event Type of data being emitted
 * @param  {Object} data  The data to emit
 */
function emitSelf(event, data) {
	if (!this.dispatcher) return data;
	this.dispatcher.emit(make_path("$" + this.prefix, this.self, event), data);
	return data;
}

/**
 * Emits data related to the room in general
 * @param  {String} event Type of event being emitted
 * @param  {Object} data  Data to emit
 */
function emitRoom(event, data) {
	if (!this.dispatcher) return data;
	this.dispatcher.emit(make_path("$" + this.prefix, event), data);
	return data;
}

/**
 * Emits data to the other participant in the call
 * @param  {String} event Type of event being emitted
 * @param  {Object} data  The data to emit
 * @return {[type]}       [description]
 */
function emitOther(event, data) {
	if (!this.dispatcher) return data;
	this.dispatcher.emit(make_path("$" + this.prefix, this.other, event), data);
	return data;
}

/**
 * Sets the state of the call
 * @param {String} state The state the call should now be in
 */
function setState(state) {
	if (this.state === state) return;
	this.state = state;
	asap(function () {
		this.emit("state", state);
	}.bind(this));
	return Promise.resolve(state);
}

/**
 * Binds a function to the call
 * @param  {Function} fn The function to bind to the `this` context
 * @return {Function}    The bound function
 */
function bind(fn) {
	return fn.bind(this);
}

/**
 * Helper for making error handlers
 * @return {Function} Function that will emit an error asynchronously
 */
function makeError() {
	var call = this;
	return function (error) {
		asap(function () {
			call.emit("error", error);
		});
	};
}

/**
 * Constructs a path for emitting an event based on a bunch of path segments
 * Uses all supplied arguments for constructing the path
 * @return {String} The path
 */
function make_path() {
	return [].join.call(arguments, "/");
}

function isSameRoom(call) {
	return this.prefix === call.prefix;
}
